/**
 * @morfi/backend-sdk-v2 · src/helpers
 *
 * Hand-written math / coercion / mapping helpers that codegen-emitted
 * spec files call into. These exist so the codegen template never
 * inlines raw arithmetic or enum-translation for realtime patches —
 * every D6/D9 op and every non-trivial payload→field mapping goes
 * through a named function the test suite pins behaviour on.
 *
 * Not part of the public SDK surface — `src/index.ts` does NOT re-export
 * these. They're for codegen output only.
 */

export { type BuiltActivityRow, buildActivityRowFromEvent } from './activity-row.js';
export { advanceStreakOnTrade } from './advance-streak-on-trade.js';
export { appendOutcomeToProposal } from './append-outcome-to-proposal.js';
export { applyAmmActivityToGraduatedRow } from './apply-amm-activity-to-graduated-row.js';
export { applyAmmActivityToMarket } from './apply-amm-activity-to-market.js';
export { applyAmmActivityToOutcomeChart } from './apply-amm-activity-to-outcome-chart.js';
export { applyAmmActivityToWinner } from './apply-amm-activity-to-winner.js';
export {
  applyBackingToBackerEarnings,
  type BackerEarningsRowLike,
} from './apply-backing-to-backer-earnings.js';
export { applyBcActivityToMarket } from './apply-bc-activity-to-market.js';
export { applyBcTradeMarkToMarket } from './apply-bc-trade-mark-to-market.js';
export { applyBoostToLeaderboardRow } from './apply-boost-to-leaderboard-row.js';
export { applyClaimToBackerEarnings } from './apply-claim-to-backer-earnings.js';
export {
  applyFollowCountsOptimistic,
  applyFollowOptimistic,
  applyUnfollowCountsOptimistic,
  applyUnfollowOptimistic,
} from './apply-follow-optimistic.js';
export { applyLeaderboardRow } from './apply-leaderboard-row.js';
export { applyOutcomePromotion } from './apply-outcome-added.js';
export { applyOutcomeSuggestionVote } from './apply-outcome-suggestion-vote.js';
export { applyOutcomeSuggestionVoteOptimistic } from './apply-outcome-suggestion-vote-optimistic.js';
export {
  applyPrivateAccountGiftIndexingOptimistic,
  applyPrivateAccountGiftsDisabledOptimistic,
  applyPrivateAccountLocationOptimistic,
  applyPrivateAccountProfileOptimistic,
  applyPrivateAccountSessionRevokeOptimistic,
} from './apply-private-account-optimistic.js';
export { applyProposalVoteOptimistic } from './apply-proposal-vote-optimistic.js';
export { applyPublicUserProfileOptimistic } from './apply-public-user-profile-optimistic.js';
export { applyThesisReactionOptimistic } from './apply-thesis-reaction-optimistic.js';
export {
  applyAmmActivityToOutcome,
  applyMarketTradeToOutcome,
} from './apply-trade-to-outcome.js';
export { applyWalletLifetimeTotals } from './apply-trade-to-user-stats.js';
export { applyViewerShareBalanceDelta } from './apply-viewer-share-balance-delta.js';
export {
  applyBlockAccountOptimistic,
  applyUnblockAccountOptimistic,
  isBlockedAccountCheckQuery,
  isContentBlockingAffectedQuery,
  reconcileContentBlockingResponse,
} from './content-blocking.js';
export {
  applyConvictionChartResolution,
  applyConvictionChartTrade,
  convictionChartSnapshotSequence,
} from './conviction-chart-owner.js';
export { addD6String, coerceD6String, isZeroD6 } from './d6.js';
export { addD9String, coerceD9String, setD9String } from './d9.js';
export { keepFollowerListUntilProtectedRefetch } from './follower-row.js';
export { incrementFollowersCount } from './followers-count.js';
export {
  applyPendingCountDelta,
  applyPendingValueDelta,
} from './gift-pending-count.js';
export {
  mapGiftEventKindToPermalinkStatus,
  markContributorClaimedOnEvent,
  setPermalinkClaimedAtIfClaimed,
} from './gift-permalink.js';
export { type BuiltGiftRow, buildGiftRowFromEvent } from './gift-row.js';
export { normalizeChartQueryArgs, resolveChartIntervalSeconds } from './live-bucket-core.js';
export { resolveLiveChartRequestArgs } from './live-chart-request.js';
export { applyLiveEventSchedule } from './live-event-schedule.js';
export { markOutcomeSuggestionPromoting } from './mark-outcome-suggestion-promoting.js';
export { buildMarketBoostEventRow } from './market-boost-event-row.js';
export { marketChartSequenceGateSubject } from './market-chart-sequence-gate.js';
export { mapLifecycleStatus } from './market-lifecycle.js';
export { mapResolvedStatus } from './market-resolution.js';
export {
  type MarketChartCandlePoint,
  type MarketChartCandlePresentation,
  type MarketChartGapPolicy,
  type MarketChartLinePoint,
  type MarketChartPresentationBounds,
  materializeMarketChartCandles,
  materializeMarketChartLine,
} from './materialize-market-chart.js';
export { mergeUserProfileSnapshot } from './merge-user-profile-snapshot.js';
export {
  type BuiltOutcomeSuggestionRow,
  buildOutcomeSuggestionRowFromEvent,
} from './outcome-suggestion-row.js';
export { mapProposalLifecycleStatus } from './proposal-lifecycle.js';
export {
  type BuiltBackerEarningsRow,
  recomputeBackerEarnings,
} from './recompute-backer-earnings.js';
export { recomputeNetVotes } from './recompute-net-votes.js';
export { retainActivityIndex } from './retain-activity-index.js';
export { retainParticipantRelation } from './retain-participant-relation.js';
export { streakDayChanged } from './streak-day-changed.js';
export {
  type BuiltTradeRow,
  buildTradeRowFromAmmEvent,
  buildTradeRowFromEvent,
} from './trade-row.js';
export { updateLiveBucket } from './update-live-bucket.js';
export { updateLiveBucketFromAmm } from './update-live-bucket-from-amm.js';

import { buildActivityRowFromEvent } from './activity-row.js';
import { advanceStreakOnTrade } from './advance-streak-on-trade.js';
import { appendOutcomeToProposal } from './append-outcome-to-proposal.js';
import { applyAmmActivityToGraduatedRow } from './apply-amm-activity-to-graduated-row.js';
import { applyAmmActivityToMarket } from './apply-amm-activity-to-market.js';
import { applyAmmActivityToOutcomeChart } from './apply-amm-activity-to-outcome-chart.js';
import { applyAmmActivityToWinner } from './apply-amm-activity-to-winner.js';
import { applyBackingToBackerEarnings } from './apply-backing-to-backer-earnings.js';
import { applyBcActivityToMarket } from './apply-bc-activity-to-market.js';
import { applyBcTradeMarkToMarket } from './apply-bc-trade-mark-to-market.js';
import { applyBoostToLeaderboardRow } from './apply-boost-to-leaderboard-row.js';
import { applyClaimToBackerEarnings } from './apply-claim-to-backer-earnings.js';
import {
  applyFollowCountsOptimistic,
  applyFollowOptimistic,
  applyUnfollowCountsOptimistic,
  applyUnfollowOptimistic,
} from './apply-follow-optimistic.js';
import { applyLeaderboardRow } from './apply-leaderboard-row.js';
import { applyOutcomePromotion } from './apply-outcome-added.js';
import { applyOutcomeSuggestionVote } from './apply-outcome-suggestion-vote.js';
import { applyOutcomeSuggestionVoteOptimistic } from './apply-outcome-suggestion-vote-optimistic.js';
import {
  applyPrivateAccountGiftIndexingOptimistic,
  applyPrivateAccountGiftsDisabledOptimistic,
  applyPrivateAccountLocationOptimistic,
  applyPrivateAccountProfileOptimistic,
  applyPrivateAccountSessionRevokeOptimistic,
} from './apply-private-account-optimistic.js';
import { applyProposalVoteOptimistic } from './apply-proposal-vote-optimistic.js';
import { applyPublicUserProfileOptimistic } from './apply-public-user-profile-optimistic.js';
import { applyThesisReactionOptimistic } from './apply-thesis-reaction-optimistic.js';
import { applyAmmActivityToOutcome, applyMarketTradeToOutcome } from './apply-trade-to-outcome.js';
import { applyWalletLifetimeTotals } from './apply-trade-to-user-stats.js';
import { applyViewerShareBalanceDelta } from './apply-viewer-share-balance-delta.js';
import {
  applyConvictionChartResolution,
  applyConvictionChartTrade,
  convictionChartSnapshotSequence,
} from './conviction-chart-owner.js';
import { keepFollowerListUntilProtectedRefetch } from './follower-row.js';
import { incrementFollowersCount } from './followers-count.js';
import { applyPendingCountDelta, applyPendingValueDelta } from './gift-pending-count.js';
import {
  mapGiftEventKindToPermalinkStatus,
  markContributorClaimedOnEvent,
  setPermalinkClaimedAtIfClaimed,
} from './gift-permalink.js';
import { buildGiftRowFromEvent } from './gift-row.js';
import { normalizeChartQueryArgs } from './live-bucket-core.js';
import { resolveLiveChartRequestArgs } from './live-chart-request.js';
import { applyLiveEventSchedule } from './live-event-schedule.js';
import { markOutcomeSuggestionPromoting } from './mark-outcome-suggestion-promoting.js';
import { buildMarketBoostEventRow } from './market-boost-event-row.js';
import { marketChartSequenceGateSubject } from './market-chart-sequence-gate.js';
import { mapLifecycleStatus } from './market-lifecycle.js';
import { mapResolvedStatus } from './market-resolution.js';
import { mergeUserProfileSnapshot } from './merge-user-profile-snapshot.js';
import { buildOutcomeSuggestionRowFromEvent } from './outcome-suggestion-row.js';
import { mapProposalLifecycleStatus } from './proposal-lifecycle.js';
import { recomputeBackerEarnings } from './recompute-backer-earnings.js';
import { recomputeNetVotes } from './recompute-net-votes.js';
import { retainActivityIndex } from './retain-activity-index.js';
import { retainParticipantRelation } from './retain-participant-relation.js';
import { streakDayChanged } from './streak-day-changed.js';
import { buildTradeRowFromAmmEvent, buildTradeRowFromEvent } from './trade-row.js';
import { updateLiveBucket } from './update-live-bucket.js';
import { updateLiveBucketFromAmm } from './update-live-bucket-from-amm.js';

/**
 * Named realtime-helper registry — the resolution target of the optional
 * `helper:` key inside a `sdk.manifest.yml` realtime `set:` / `add:`
 * recipe.
 *
 * Most realtime subjects map 1:1 (`add: { volumeTotal: amountD6 }` — direct D6
 * accumulate, no helper; conviction is a declarative nested-array `set:`
 * snapshot, no helper). A helper is named only when the payload→field
 * mapping needs logic the declarative recipe can't express:
 *   - `morfi.market.{m}.lifecycle.v0` → `mapLifecycleStatus` (translates
 *     the `MarketLifecycle.kind` enum to the market `status` vocabulary).
 *   - `morfi.market.{m}.user.*.trade.v0` → `applyBcActivityToMarket`
 *     (patches the traded outcome's current bonding-curve state, derived
 *     market cap, and emitted authoritative real-reserve total).
 *
 * Most helpers are `(prevRow, msg) => nextRow`: `prevRow` is the cached
 * row, `msg` is the decoded protobuf message for that subject. Helpers
 * are immutable and bail to the exact `prevRow` reference when nothing
 * changes (keeps structural-sharing short-circuits intact).
 *
 * One helper has a different shape — the `prepend:` row builder:
 *   - `buildTradeRowFromEvent(msg, subject) => row` constructs a brand-new
 *     list row from a trade event for the `useGetTrades` live tape (the
 *     manifest `prepend:` recipe — see `scripts/gen.ts`). It takes the
 *     decoded message + the concrete subject, not a `prevRow`.
 *
 * Codegen (`scripts/gen.ts`) emits `HELPERS.<name>(...)` for a
 * subscription whose recipe carries `helper: <name>` or `prepend: <name>`.
 * `load.ts` validates the name against `HELPER_NAMES` at `pnpm gen` time
 * so an unknown helper fails the build, never ships an unreachable branch.
 */
export const HELPERS = {
  applyLiveEventSchedule,
  mapLifecycleStatus,
  mapResolvedStatus,
  applyBcActivityToMarket,
  buildTradeRowFromEvent,
  buildTradeRowFromAmmEvent,
  buildMarketBoostEventRow,
  applyBoostToLeaderboardRow,
  applyAmmActivityToMarket,
  applyAmmActivityToOutcomeChart,
  applyAmmActivityToOutcome,
  applyMarketTradeToOutcome,
  applyBcTradeMarkToMarket,
  applyAmmActivityToGraduatedRow,
  applyAmmActivityToWinner,
  applyViewerShareBalanceDelta,
  applyConvictionChartTrade,
  applyConvictionChartResolution,
  convictionChartSnapshotSequence,
  advanceStreakOnTrade,
  applyWalletLifetimeTotals,
  mergeUserProfileSnapshot,
  streakDayChanged,
  recomputeNetVotes,
  mapProposalLifecycleStatus,
  keepFollowerListUntilProtectedRefetch,
  incrementFollowersCount,
  buildActivityRowFromEvent,
  buildGiftRowFromEvent,
  buildOutcomeSuggestionRowFromEvent,
  applyOutcomeSuggestionVoteOptimistic,
  applyOutcomeSuggestionVote,
  applyProposalVoteOptimistic,
  applyPublicUserProfileOptimistic,
  applyPrivateAccountProfileOptimistic,
  applyPrivateAccountLocationOptimistic,
  applyPrivateAccountGiftsDisabledOptimistic,
  applyPrivateAccountGiftIndexingOptimistic,
  applyPrivateAccountSessionRevokeOptimistic,
  applyThesisReactionOptimistic,
  applyFollowOptimistic,
  applyUnfollowOptimistic,
  applyFollowCountsOptimistic,
  applyUnfollowCountsOptimistic,
  applyOutcomePromotion,
  appendOutcomeToProposal,
  markOutcomeSuggestionPromoting,
  updateLiveBucket,
  updateLiveBucketFromAmm,
  marketChartSequenceGateSubject,
  resolveLiveChartRequestArgs,
  normalizeChartQueryArgs,
  mapGiftEventKindToPermalinkStatus,
  setPermalinkClaimedAtIfClaimed,
  markContributorClaimedOnEvent,
  applyPendingCountDelta,
  applyPendingValueDelta,
  recomputeBackerEarnings,
  retainParticipantRelation,
  retainActivityIndex,
  applyBackingToBackerEarnings,
  applyClaimToBackerEarnings,
  applyLeaderboardRow,
} as const;

/** Valid `helper:` / `prepend:` recipe names — the build-time allowlist
 *  `load.ts` cross-checks every realtime recipe helper against. */
export const HELPER_NAMES = [
  'applyLiveEventSchedule',
  'mapLifecycleStatus',
  'mapResolvedStatus',
  'applyBcActivityToMarket',
  'buildTradeRowFromEvent',
  'buildTradeRowFromAmmEvent',
  'buildMarketBoostEventRow',
  'applyBoostToLeaderboardRow',
  'applyAmmActivityToMarket',
  'applyAmmActivityToOutcomeChart',
  'applyAmmActivityToOutcome',
  'applyMarketTradeToOutcome',
  'applyBcTradeMarkToMarket',
  'applyAmmActivityToGraduatedRow',
  'applyAmmActivityToWinner',
  'applyViewerShareBalanceDelta',
  'applyConvictionChartTrade',
  'applyConvictionChartResolution',
  'convictionChartSnapshotSequence',
  'advanceStreakOnTrade',
  'applyWalletLifetimeTotals',
  'mergeUserProfileSnapshot',
  'streakDayChanged',
  'recomputeNetVotes',
  'mapProposalLifecycleStatus',
  'keepFollowerListUntilProtectedRefetch',
  'incrementFollowersCount',
  'buildActivityRowFromEvent',
  'buildGiftRowFromEvent',
  'buildOutcomeSuggestionRowFromEvent',
  'applyOutcomeSuggestionVoteOptimistic',
  'applyOutcomeSuggestionVote',
  'applyProposalVoteOptimistic',
  'applyPublicUserProfileOptimistic',
  'applyPrivateAccountProfileOptimistic',
  'applyPrivateAccountLocationOptimistic',
  'applyPrivateAccountGiftsDisabledOptimistic',
  'applyPrivateAccountGiftIndexingOptimistic',
  'applyPrivateAccountSessionRevokeOptimistic',
  'applyThesisReactionOptimistic',
  'applyFollowOptimistic',
  'applyUnfollowOptimistic',
  'applyFollowCountsOptimistic',
  'applyUnfollowCountsOptimistic',
  'applyOutcomePromotion',
  'appendOutcomeToProposal',
  'markOutcomeSuggestionPromoting',
  'updateLiveBucket',
  'updateLiveBucketFromAmm',
  'mapGiftEventKindToPermalinkStatus',
  'setPermalinkClaimedAtIfClaimed',
  'markContributorClaimedOnEvent',
  'applyPendingCountDelta',
  'applyPendingValueDelta',
  'recomputeBackerEarnings',
  'retainParticipantRelation',
  'retainActivityIndex',
  'applyBackingToBackerEarnings',
  'applyClaimToBackerEarnings',
  'applyLeaderboardRow',
] as const;
export type HelperName = (typeof HELPER_NAMES)[number];
